import { eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@cashsync/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ─────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can match
 * **ZERO rows and return "success"**. Confirmed in canon
 * (`listto-app` `9ae347b6`, measured origin in `space-academy-app`): a course
 * is created, the unit is created 1.03 s later, and in between **no CRUD
 * op exists** — the title the author had just written never generated a CRUD
 * operation.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the local
 * view from the last checkpoint**. If the checkpoint does not yet bring the
 * newly created row (wallet, category, transaction…), the row is **ABSENT
 * during that instant** — and an UPDATE against it writes nothing, generates no
 * op, and **does not fail**.
 *
 * In `cash-sync` this is not a mistyped title: it is money. A user who
 * creates a transaction and corrects the amount, account, or category in the
 * same session (the normal flow when quickly logging an expense and tuning it)
 * trusts that this adjustment was saved. If it falls in the window, the mistyped
 * amount, wrong account, or unassigned category **remain as-is without any
 * warning**.
 *
 * Why the absence of an error proves nothing here: the server write path
 * deliberately always returns 2xx (a 4xx would block the client queue
 * forever). That is a DIFFERENT path —there the server rejects and stays silent,
 * here the local UPDATE does not match—, but the failure mode is the same: a
 * user write that disappears without a trace.
 *
 * Contract of this module: write, **REREAD**, compare. If the value did not
 * land it retries with backoff; if upon exhaustion it still has not landed, it
 * **THROWS** — caller communicates it instead of leaving user staring at a
 * field that represents nothing saved.
 *
 * ── What this module does NOT cover (deliberate) ───────────────────────────
 * Hard `DELETE`s (`purgeTransactionMutation`, `purgeWalletMutation`,
 * `purgeCategoryMutation`, `deleteBudgetMutation`, `deleteRecurringRuleMutation`,
 * `deletePayeeRuleMutation`, `deleteTxTemplateMutation`, `deleteAttachmentMutation`)
 * are not verified: their natural check would be "row is no longer present", and
 * "absent" is precisely the ambiguous state of this window — cannot distinguish
 * "was deleted" from "had not arrived yet". Verifying them would require another signal
 * (own CRUD queue), not a reread. Signal that would reopen it: a purge that
 * is genuinely lost (row reappears in trash after purge).
 *
 * ⚠️ `user` IS NO LONGER excluded (2026-08-13). The argument that "the server
 * creates it upon signup, so it is already in the checkpoint before the
 * first preference" was copied from canon without verification and is FALSE here:
 * `db` (PowerSync accessor) becomes non-null as soon as local SQLite opens,
 * `userId()` activates with the REST response of `signUpActor` —
 * independent of the sync stream— and `ThemeProvider`'s `createEffect`
 * (`apps/web/src/providers/theme-provider.tsx`) seeds the local row for
 * ANY `uid` without waiting for `hasSynced`. There is a window just like in
 * `wallet`/`tx`/etc. Audit: `infra/reports/user-exclusion-audit/2026-08-13-summary.md`.
 * The 6 preferences mutations (`packages/shared/src/users/mutations.ts`)
 * and `updateUserFinanceSettingsMutation` (`../finance/mutations.ts`) go through
 * `patchRowVerified`.
 */

/**
 * Retry backoff. Sums ≈ 6.05 s: more than covers the round trip of a
 * checkpoint without leaving a quick interaction —correcting an amount, marking
 * a transaction reconciled— hanging forever if the row truly does not
 * exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface VerifiedWriteOptions {
  /** Delays between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
}

/** Error for a local-first write that failed to land in view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Reads the row by id, or `undefined` if it is not in the local view. */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read
  // primary key, so the row is a flat record of columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const rows = (await db
    .select()
    .from(table)
    .where(eq(idColumn, id))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when comparing against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, rereads, compares the patch
 * fields and retries as long as they have not landed. Throws `LocalWriteLostError`
 * if they are still missing upon exhausting the backoff.
 *
 * `updated_at` is set by this function on every attempt (and is not compared: it changes
 * each pass); `patch` must contain only domain fields.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(eq(idColumn, id));

    const row = await readRow(db, table, idColumn, id);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Waits until the row EXISTS in the local view before touching it.
 *
 * For mutations combining an `UPDATE` with a sibling `INSERT` inside
 * the SAME `db.transaction(...)` —there retrying the `UPDATE` is not valid (would
 * sleep holding write lock), and damage of operating on an
 * absent row is worse than losing a field: the UPDATE matches nothing but the
 * sibling INSERT still occurs, leaving orphaned or duplicate data. No
 * mutation in `cash-sync` currently has this shape (verified: the only
 * `db.transaction(...)` in the financial domain are `restoreBackup` and
 * `runAndroMoneyImport`, and neither pairs an UPDATE with a sibling INSERT
 * on a row created in the same session — `restoreBackup` rereads existing
 * ids within the transaction itself right before updating them).
 * Left here, alongside `patchRowVerified`, for the next mutation that
 * does have this shape — e.g. if the recurring engine was changed to complete an
 * occurrence and generate the next one within a transaction.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}
